import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { AccessibilityRole } from 'react-native';

import { isWeb } from 'utils/environment';

const FOCUSABLE_MODAL_ELEMENT_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
const MODAL_BACKDROP_DATA_ATTRIBUTE = 'data-modal-focus-trap-backdrop';
// aria-modal="true" alone (regardless of role) reliably marks a nested modal boundary: RN Web's
// own Modal sets it on its role="dialog" content (ModalContent.js), but NativeBase's AlertDialog
// content sets accessibilityRole="alert" instead of "dialog" (AlertDialogContent.tsx) while still
// carrying aria-modal="true" — matching on aria-modal alone covers both without special-casing
// every accessibilityRole a nested modal might use.
const NESTED_MODAL_SELECTOR = '[aria-modal="true"]';
// Braze renders an in-app message into its own container appended to document.body, as a sibling
// of the app root, marked role="complementary" — so NESTED_MODAL_SELECTOR can't recognize it, and
// hiding it marks the whole message iframe `inert`, which kills pointer events and leaves a
// visible but unclickable message. An in-app message is an independent overlay that owns its own
// interaction rather than background content for a BK modal, so it's exempt regardless of whether
// it mounted before or after this trap activated. Both container classes Braze has shipped are
// matched, mirroring the version spread braze-css-bug-override.web.tsx already accounts for.
const BRAZE_IN_APP_MESSAGE_SELECTOR = '.ab-iam-root, .ab-centering-div';
// OneTrust appends its banner and preference center to document.body, outside the app root and
// without a marker this trap already treats as a nested modal. Marking that container `inert`
// disables its pointer events, so the dialog stays visible while clicks land on the page behind
// it. It is an independent overlay, exempt whether it was already open or appears later.
const ONETRUST_CONSENT_SELECTOR = '#onetrust-consent-sdk, #onetrust-banner-sdk, #onetrust-pc-sdk';

const getActiveHtmlElement = () =>
  document.activeElement instanceof HTMLElement ? document.activeElement : null;

const isOrContainsNestedModal = (element: HTMLElement) =>
  element.matches(NESTED_MODAL_SELECTOR) || !!element.querySelector(NESTED_MODAL_SELECTOR);

const isOrContainsBrazeInAppMessage = (element: HTMLElement) =>
  element.matches(BRAZE_IN_APP_MESSAGE_SELECTOR) ||
  !!element.querySelector(BRAZE_IN_APP_MESSAGE_SELECTOR);

const isOrContainsOneTrustConsent = (element: HTMLElement) =>
  element.matches(ONETRUST_CONSENT_SELECTOR) || !!element.querySelector(ONETRUST_CONSENT_SELECTOR);

const isExternalOverlay = (element: HTMLElement) =>
  isOrContainsBrazeInAppMessage(element) || isOrContainsOneTrustConsent(element);

// Timing-independent fallback for handleFocusIn/handleKeyDown: rather than only trusting
// focusExemptContainers (populated asynchronously by hideElementsOutsideModal's
// MutationObserver — see reevaluateHiddenElementsForExemptions), this checks the live DOM
// directly at the moment focus moves. A nested modal's own focus-trapping (e.g. NativeBase's
// FocusScope) can autofocus into it before that MutationObserver's microtask has run, which on
// some browsers loses the race against the very first focus event — this check has no such race,
// since it doesn't depend on anything having run beforehand. Walks up from target to find its
// topmost ancestor under document.body (the portal wrapper, covering ModalFocusTrap's focus
// bracket siblings too, not just descendants of the aria-modal node), then checks whether that
// wrapper is a nested modal and isn't (part of) this trap's own modalElement.
//
// isPreExistingBackgroundElement guards against exempting a modal that already existed (and was
// deliberately hidden as background content) before this trap activated — that decision is made
// synchronously by hideElementsOutsideModal's initial snapshot pass, so it's just as safe to
// trust here as the live DOM shape itself; only a modal that opened after activation should ever
// bypass this trap's focus containment.
const isInsideLiveNestedModal = (
  target: HTMLElement,
  modalElement: HTMLElement,
  isPreExistingBackgroundElement: (element: HTMLElement) => boolean
) => {
  if (modalElement.contains(target)) {
    return false;
  }

  let topmostAncestor: HTMLElement = target;

  while (topmostAncestor.parentElement && topmostAncestor.parentElement !== document.body) {
    topmostAncestor = topmostAncestor.parentElement;
  }

  // topmostAncestor containing modalElement (rather than being a separate nested modal) means
  // target is simply a sibling of modalElement under the same non-body ancestor — e.g. a plain
  // page element that happens to share a wrapper with this trap's own modal — not a nested modal.
  if (topmostAncestor.contains(modalElement)) {
    return false;
  }

  if (isPreExistingBackgroundElement(topmostAncestor)) {
    return false;
  }

  return isOrContainsNestedModal(topmostAncestor);
};

const getModalFocusableElements = (modalElement: HTMLElement) =>
  Array.from(modalElement.querySelectorAll<HTMLElement>(FOCUSABLE_MODAL_ELEMENT_SELECTOR)).filter(
    element =>
      !element.hasAttribute('disabled') &&
      element.tabIndex !== -1 &&
      !element.closest('[aria-hidden="true"], [inert]')
  );

const isFocusableElementVisible = (element: HTMLElement) => {
  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    const style = window.getComputedStyle(currentElement);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    currentElement = currentElement.parentElement;
  }

  return true;
};

const focusFirstModalElement = (modalElement: HTMLElement) => {
  const [firstFocusableElement] = getModalFocusableElements(modalElement);

  (firstFocusableElement ?? modalElement).focus();
};

// Hides (and un-hides on cleanup) every DOM sibling outside the modal's ancestor chain, up to
// document.body, so screen readers and Tab navigation can't reach content behind the modal. A
// MutationObserver keeps hiding siblings mounted after the initial snapshot (e.g. a toast portal
// appended while the modal is open) so they can't steal focus away from the trapped modal either.
const hideElementsOutsideModal = (modalElement: HTMLElement) => {
  const restoreElementCallbacks: Array<() => void> = [];
  const mutationObservers: MutationObserver[] = [];
  let currentElement: HTMLElement | null = modalElement;

  // Elements this trap has hidden, keyed so a later change can find and undo the hide
  // retroactively (see reevaluateHiddenElementsForExemptions below). allowNestedModalExemption
  // is recorded per-entry so that re-check only applies to elements inserted after this trap
  // activated — pre-existing/background content stays hidden even if it changes later, since it
  // isn't something opened from within this modal.
  const hiddenElements = new Map<
    HTMLElement,
    { restore: () => void; allowNestedModalExemption: boolean }
  >();

  // Containers exempted from hiding: a nested modal that opened after this trap activated, or an
  // external overlay (a Braze in-app message or the OneTrust consent UI). Exposed via
  // isInsideFocusExemptContainer so handleFocusIn/handleKeyDown can tell "focus moved into a
  // surface that owns it" from "focus escaped" by containment, not just a role match — covering
  // siblings of the dialog node too (react-native-web's ModalFocusTrap focus brackets sit next
  // to, not inside, the role="dialog" element).
  const focusExemptContainers = new Set<HTMLElement>();
  // Kept separately from focusExemptContainers so modal boundary traversal can enter a Braze
  // message or the OneTrust dialog without changing how Tab behaves for nested React Native Modals.
  const externalOverlayContainers = new Set<HTMLElement>();

  const unhideElement = (element: HTMLElement) => {
    hiddenElements.get(element)?.restore();
    hiddenElements.delete(element);
  };

  const hideElement = (
    element: Element,
    modalAncestorElement: HTMLElement,
    allowNestedModalExemption: boolean
  ) => {
    if (
      !(element instanceof HTMLElement) ||
      element === modalAncestorElement ||
      element.hasAttribute(MODAL_BACKDROP_DATA_ATTRIBUTE) ||
      hiddenElements.has(element)
    ) {
      return;
    }

    // Unlike the nested-modal exemption below, this one ignores allowNestedModalExemption: a
    // Braze message or OneTrust dialog already on screen when this trap activates is still an
    // active overlay the guest is interacting with, not stale background content this modal
    // should isolate.
    if (isExternalOverlay(element)) {
      focusExemptContainers.add(element);
      externalOverlayContainers.add(element);
      return;
    }

    if (allowNestedModalExemption && isOrContainsNestedModal(element)) {
      // Any React Native <Modal> portals straight to document.body on web, landing as a
      // sibling of the app root rather than a DOM descendant of this modal. Hiding it here
      // would mark it `inert`, which disables pointer events too — so a nested modal (e.g. the
      // language selector opened from within this trapped drawer) would render on top but
      // silently swallow every click.
      focusExemptContainers.add(element);
      return;
    }

    const previousAriaHidden = element.getAttribute('aria-hidden');
    const hadInertAttribute = element.hasAttribute('inert');

    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', '');

    const restore = () => {
      // A native React Native Web Modal can manage the same attributes while this trap is
      // active. If it changed either one after our snapshot (for example, removing the temporary
      // aria-hidden it put on a parent drawer as a child modal closes), that newer state wins.
      // Restoring a stale value here would leave the parent dialog permanently hidden.
      if (element.getAttribute('aria-hidden') === 'true') {
        if (previousAriaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', previousAriaHidden);
        }
      }

      if (!hadInertAttribute && element.hasAttribute('inert')) {
        element.removeAttribute('inert');
      }
    };

    hiddenElements.set(element, { restore, allowNestedModalExemption });
    restoreElementCallbacks.push(restore);
  };

  // A React Native Modal's portal wrapper can be appended to document.body empty and only gain
  // role="dialog" on its content a render pass later (an attributes mutation), or a dialog
  // subtree can mount fully-formed as a descendant insertion under an already-hidden wrapper
  // (a childList mutation the observer's direct-child check above doesn't see). Either way, the
  // childList observer's very first pass can hide the wrapper before it's recognizable as a
  // nested modal, so this re-evaluates every previously-hidden element on both kinds of change,
  // un-hiding it if it (or a descendant) has since become exempt. The nested-modal branch only
  // reconsiders elements that were eligible for that exemption in the first place (i.e. inserted
  // after this trap activated), so pre-existing background content stays hidden; the in-app
  // message branch has no such restriction, matching hideElement above.
  const reevaluateHiddenElementsForExemptions = () => {
    Array.from(hiddenElements.entries()).forEach(([element, { allowNestedModalExemption }]) => {
      if (!document.contains(element)) {
        hiddenElements.delete(element);
        return;
      }

      // Braze decorates its container across several mutations (the message iframe lands as a
      // later descendant insertion, and the `ab-show` class arrives later still). OneTrust can
      // likewise stamp its container id after the node is appended. A pass that ran before the
      // container was recognizable must be able to take the hide back.
      if (isExternalOverlay(element)) {
        focusExemptContainers.add(element);
        externalOverlayContainers.add(element);
        unhideElement(element);
        return;
      }

      if (allowNestedModalExemption && isOrContainsNestedModal(element)) {
        focusExemptContainers.add(element);
        unhideElement(element);
      }
    });
  };

  while (currentElement?.parentElement) {
    const parentElement: HTMLElement = currentElement.parentElement;
    const modalAncestorElement: HTMLElement = currentElement;

    // Elements already on screen when this trap activates are pre-existing/background content
    // (not something opened from within this now-active modal), so hide them unconditionally —
    // no nested-modal exemption. Only nodes inserted afterward (below) get the exemption.
    Array.from(parentElement.children).forEach(element =>
      hideElement(element, modalAncestorElement, false)
    );

    const mutationObserver = new MutationObserver(mutations => {
      // See reevaluateHiddenElementsForExemptions above for why both attribute changes and
      // descendant insertions need to trigger a re-check.
      let shouldReevaluateHiddenElements = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'attributes') {
          // Ignore normal class/role churn inside the active modal or other visible UI. An
          // attribute mutation can only reveal an exemption when it occurs on an element this
          // trap hid, or somewhere inside that hidden subtree.
          const target = mutation.target;
          if (
            Array.from(hiddenElements.keys()).some(
              element => element === target || element.contains(target)
            )
          ) {
            shouldReevaluateHiddenElements = true;
          }
          return;
        }

        mutation.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.parentElement === parentElement) {
            hideElement(node, modalAncestorElement, true);
          } else {
            shouldReevaluateHiddenElements = true;
          }
        });
      });

      if (shouldReevaluateHiddenElements) {
        reevaluateHiddenElementsForExemptions();
      }
    });

    mutationObserver.observe(parentElement, {
      childList: true,
      attributes: true,
      // Braze can append a populated container first and assign its identifying class in a
      // later task. OneTrust can assign its container id the same way. Those changes must
      // re-trigger exemption re-evaluation.
      attributeFilter: ['role', 'aria-modal', 'class', 'id'],
      subtree: true,
    });
    mutationObservers.push(mutationObserver);

    if (parentElement === document.body) {
      break;
    }

    currentElement = parentElement;
  }

  const isInsideFocusExemptContainer = (target: HTMLElement) =>
    Array.from(focusExemptContainers).some(container => container.contains(target));

  const focusExternalOverlayBoundary = (reverse: boolean) => {
    const focusableElements = Array.from(externalOverlayContainers)
      .filter(container => document.contains(container))
      .flatMap(container => getModalFocusableElements(container))
      .filter(isFocusableElementVisible);
    const target = reverse ? focusableElements[focusableElements.length - 1] : focusableElements[0];
    const previousActiveElement = getActiveHtmlElement();

    target?.focus();
    const nextActiveElement = getActiveHtmlElement();

    return Boolean(
      target &&
        nextActiveElement &&
        nextActiveElement !== previousActiveElement &&
        (nextActiveElement === target || target.contains(nextActiveElement))
    );
  };

  // Elements hidden with allowNestedModalExemption: false were already on screen when this trap
  // activated — deliberately treated as background content regardless of what they contain (see
  // hideElement above). isInsideLiveNestedModal must never override that, or a pre-existing
  // hidden dialog could still pull focus out of this trap.
  const isPreExistingBackgroundElement = (element: HTMLElement) =>
    hiddenElements.get(element)?.allowNestedModalExemption === false;

  const cleanup = () => {
    mutationObservers.forEach(observer => observer.disconnect());
    restoreElementCallbacks.reverse().forEach(restoreElement => restoreElement());
    focusExemptContainers.clear();
    externalOverlayContainers.clear();
  };

  return {
    cleanup,
    focusExternalOverlayBoundary,
    isInsideFocusExemptContainer,
    isPreExistingBackgroundElement,
  };
};

interface UseModalFocusTrapOptions {
  /** Whether the modal is currently open and should trap focus (web only; a no-op elsewhere). */
  active: boolean;
  /** Accessible label announced for the modal region. */
  label: string;
  /** Which element should receive focus when the modal first opens. */
  initialFocusTarget?: 'firstFocusable' | 'none';
  /**
   * Shared with the caller's own close handling: set to `true` while a close animation is
   * in flight, or while focus is being transferred to a child modal, so Tab-loop and
   * focus-steal handling don't fight an element that's mid-teardown. Focus restoration is also
   * skipped while this is `true`. Reset to `false` by this hook whenever the trap (re)activates.
   */
  isClosingRef: MutableRefObject<boolean>;
  /** Set to `true` when deactivation should not restore focus because a child modal is opening. */
  suppressFocusRestorationRef?: MutableRefObject<boolean>;
  /** Preserve the original trigger when this modal regains focus after a child modal closes. */
  preserveFocusTriggerRef?: MutableRefObject<boolean>;
}

interface GetModalA11yPropsOptions {
  /** Accessibility role to use on native platforms, where 'dialog' isn't a valid role. */
  nativeRole: AccessibilityRole;
}

export const useModalFocusTrap = ({
  active,
  label,
  initialFocusTarget = 'firstFocusable',
  isClosingRef,
  preserveFocusTriggerRef,
  suppressFocusRestorationRef,
}: UseModalFocusTrapOptions) => {
  const modalElementRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const restoreOutsideElementsRef = useRef<(() => void) | null>(null);
  const isInsideFocusExemptContainerRef = useRef<(target: HTMLElement) => boolean>(() => false);
  const isPreExistingBackgroundElementRef = useRef<(element: HTMLElement) => boolean>(() => false);
  const internalSuppressFocusRestorationRef = useRef(false);
  const focusRestorationSuppressedRef =
    suppressFocusRestorationRef ?? internalSuppressFocusRestorationRef;

  const modalRef = useCallback((element: unknown) => {
    modalElementRef.current = element as HTMLElement | null;
  }, []);

  const restoreOutsideElements = useCallback(() => {
    restoreOutsideElementsRef.current?.();
    restoreOutsideElementsRef.current = null;
    isInsideFocusExemptContainerRef.current = () => false;
    isPreExistingBackgroundElementRef.current = () => false;
  }, []);

  const restoreFocusToTrigger = useCallback(() => {
    if (!isWeb) {
      return;
    }

    const previouslyFocusedElement = previouslyFocusedElementRef.current;
    previouslyFocusedElementRef.current = null;

    if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
      // React Native Web can put focus on a screen's dialog root while a child route is opening.
      // Restoring to that root when the child closes leaves keyboard users at a non-actionable
      // container. A dialog root is a valid boundary, but its first control is the usable focus
      // destination for resuming the parent modal.
      if (previouslyFocusedElement.matches(NESTED_MODAL_SELECTOR)) {
        focusFirstModalElement(previouslyFocusedElement);
      } else {
        previouslyFocusedElement.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (!isWeb || !active) {
      return;
    }

    const modalElement = modalElementRef.current;

    if (!modalElement) {
      return;
    }

    isClosingRef.current = false;
    focusRestorationSuppressedRef.current = false;
    const shouldPreserveFocusTrigger = preserveFocusTriggerRef?.current;
    if (shouldPreserveFocusTrigger) {
      preserveFocusTriggerRef.current = false;
    } else {
      previouslyFocusedElementRef.current = getActiveHtmlElement();
    }
    restoreOutsideElements();

    const {
      cleanup,
      focusExternalOverlayBoundary,
      isInsideFocusExemptContainer,
      isPreExistingBackgroundElement,
    } = hideElementsOutsideModal(modalElement);
    restoreOutsideElementsRef.current = cleanup;
    isInsideFocusExemptContainerRef.current = isInsideFocusExemptContainer;
    isPreExistingBackgroundElementRef.current = isPreExistingBackgroundElement;

    const hasUsableFocusInsideModal = () => {
      const activeElement = getActiveHtmlElement();

      return (
        !!activeElement &&
        activeElement !== modalElement &&
        modalElement.contains(activeElement) &&
        !activeElement.closest('[aria-hidden="true"], [inert]')
      );
    };
    const focusModalWhenReady = () => {
      if (hasUsableFocusInsideModal()) {
        return true;
      }

      const [firstFocusableElement] = getModalFocusableElements(modalElement);

      if (!firstFocusableElement) {
        return false;
      }

      firstFocusableElement.focus();
      return true;
    };
    let focusRecoveryObserver: MutationObserver | null = null;

    // A modal can mount or resume while a child route is still removing aria-hidden/inert from
    // its subtree. At that instant there are no valid descendants to focus and the browser leaves
    // focus on the modal container. Retry when the DOM changes rather than depending on a fixed
    // transition delay; this also covers screens remounted by navigation, where ref state is new.
    if (initialFocusTarget !== 'none') {
      focusRecoveryObserver = new MutationObserver(() => {
        if (focusModalWhenReady()) {
          focusRecoveryObserver?.disconnect();
        }
      });
      focusRecoveryObserver.observe(document.body, {
        childList: true,
        attributes: true,
        attributeFilter: ['aria-hidden', 'inert'],
        subtree: true,
      });
    }

    const focusTimeout =
      initialFocusTarget === 'none'
        ? null
        : setTimeout(() => {
            if (focusModalWhenReady()) {
              focusRecoveryObserver?.disconnect();
            }
          }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || isClosingRef.current) {
        return;
      }

      const focusableElements = getModalFocusableElements(modalElement);

      if (!focusableElements.length) {
        event.preventDefault();
        modalElement.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = getActiveHtmlElement();
      const isActiveElementOutsideModal = !activeElement || !modalElement.contains(activeElement);

      // A nested route can restore focus to the parent dialog root instead of to a descendant.
      // The root is intentionally focusable for accessibility, but isn't part of the keyboard
      // tab sequence. Explicitly move the next Tab into the first control of the parent.
      if (activeElement === modalElement) {
        event.preventDefault();
        firstFocusableElement.focus();
        return;
      }

      if (
        isActiveElementOutsideModal &&
        activeElement &&
        (isInsideFocusExemptContainerRef.current(activeElement) ||
          isInsideLiveNestedModal(
            activeElement,
            modalElement,
            isPreExistingBackgroundElementRef.current
          ))
      ) {
        // Focus is inside a nested modal (see handleFocusIn below) — that modal owns Tab
        // handling for its own contents now, so redirecting Tab back into the outer modal would
        // trap keyboard users inside it.
        return;
      }

      if (isActiveElementOutsideModal) {
        event.preventDefault();
        firstFocusableElement.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        if (focusExternalOverlayBoundary(true)) {
          return;
        }
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        if (focusExternalOverlayBoundary(false)) {
          return;
        }
        firstFocusableElement.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (isClosingRef.current) {
        return;
      }

      const target = event.target;

      if (target === modalElement) {
        focusFirstModalElement(modalElement);
        return;
      }

      if (target instanceof HTMLElement && modalElement.contains(target)) {
        return;
      }

      // Focus moving into a nested modal (e.g. the language selector opened from within this
      // trapped drawer) means that modal legitimately owns focus now, so stealing it back would
      // fight that modal's own focus containment in an infinite loop. isInsideFocusExemptContainerRef
      // is the async check (see hideElementsOutsideModal); isInsideLiveNestedModal is the
      // synchronous fallback that can't lose a timing race against it (see its own comment).
      if (
        target instanceof HTMLElement &&
        (isInsideFocusExemptContainerRef.current(target) ||
          isInsideLiveNestedModal(target, modalElement, isPreExistingBackgroundElementRef.current))
      ) {
        return;
      }

      focusFirstModalElement(modalElement);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
      focusRecoveryObserver?.disconnect();
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      restoreOutsideElements();
      if (!isClosingRef.current && !focusRestorationSuppressedRef.current) {
        restoreFocusToTrigger();
      }
    };
  }, [
    active,
    focusRestorationSuppressedRef,
    initialFocusTarget,
    isClosingRef,
    preserveFocusTriggerRef,
    restoreFocusToTrigger,
    restoreOutsideElements,
  ]);

  const getModalA11yProps = useCallback(
    ({ nativeRole }: GetModalA11yPropsOptions) => ({
      accessibilityLabel: label,
      // 'dialog' is a valid web ARIA role passed through as-is by react-native-web, but isn't
      // part of RN's AccessibilityRole type (which only covers native roles), hence the cast.
      accessibilityRole: (isWeb ? 'dialog' : nativeRole) as AccessibilityRole,
      accessibilityViewIsModal: true,
      'aria-modal': isWeb ? true : undefined,
      tabIndex: -1 as const,
    }),
    [label]
  );

  const getBackdropA11yProps = useCallback(
    () => ({
      accessible: false,
      accessibilityElementsHidden: true,
      'aria-hidden': true,
      // Marks the backdrop so hideElementsOutsideModal skips it (it's a legitimate sibling of
      // the modal, not part of the page content the trap is meant to hide).
      dataSet: { modalFocusTrapBackdrop: 'true' },
      focusable: false,
      importantForAccessibility: 'no-hide-descendants' as const,
    }),
    []
  );

  return {
    modalRef,
    restoreOutsideElements,
    restoreFocusToTrigger,
    getModalA11yProps,
    getBackdropA11yProps,
  };
};
